import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scale } from 'lucide-react';
import { convertWeight, isPlausibleWeightKg } from '@/lib/measurement/units';

interface WeightInputProps {
  label?: string;
  value?: number; // weight in kg (base unit)
  onChange: (kg: number) => void;
  unit?: 'kg' | 'lbs';
  allowUnitChange?: boolean;
  className?: string;
  placeholder?: string;
}

export const WeightInput: React.FC<WeightInputProps> = ({
  label,
  value = 0,
  onChange,
  unit = 'kg',
  allowUnitChange = true,
  className,
  placeholder
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [currentUnit, setCurrentUnit] = useState(unit);
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Convert kg to display unit
  const kgToDisplayUnit = (kg: number, targetUnit: string): number => {
    return targetUnit === 'lbs' ? convertWeight(kg, 'kg', 'lbs') : kg;
  };

  // Convert display unit to kg
  const displayUnitToKg = (weight: number, sourceUnit: string): number => {
    return sourceUnit === 'lbs' ? convertWeight(weight, 'lbs', 'kg') : weight;
  };
  // Update display when value or unit changes (but not during user typing)
  useEffect(() => {
    if (!isUserTyping && value > 0) {
      const display = kgToDisplayUnit(value, currentUnit);
      setDisplayValue(display.toFixed(1));
    } else if (!isUserTyping && value === 0) {
      setDisplayValue('');
    }
  }, [value, currentUnit, isUserTyping]);

  const handleValueChange = (newValue: string) => {
    setIsUserTyping(true);
    setDisplayValue(newValue);
    
    const weight = parseFloat(newValue);
    if (!isNaN(weight) && weight > 0) {
      const kg = displayUnitToKg(weight, currentUnit);
      onChange(kg);
    } else if (newValue === '') {
      onChange(0);
    }
  };

  const handleBlur = () => {
    setIsUserTyping(false);
    // Format the value on blur if it's a valid number
    const weight = parseFloat(displayValue);
    if (!isNaN(weight) && weight > 0) {
      const formatted = weight.toFixed(1);
      setDisplayValue(formatted);
    }
  };

  const handleUnitChange = (newUnit: string) => {
    const weight = parseFloat(displayValue);
    if (!isNaN(weight) && weight > 0) {
      // Convert current display value through kg to new unit
      const kg = displayUnitToKg(weight, currentUnit);
      const newDisplayValue = kgToDisplayUnit(kg, newUnit);
      setDisplayValue(newDisplayValue.toFixed(1));
    }
    setCurrentUnit(newUnit as 'kg' | 'lbs');
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    return currentUnit === 'kg' ? '70.0' : '154.0';
  };

  const getValidationMessage = () => {
    const weight = parseFloat(displayValue);
    if (displayValue && !isNaN(weight)) {
      const kg = displayUnitToKg(weight, currentUnit);
      if (!isPlausibleWeightKg(kg)) return 'Weight seems outside typical range';
    }
    return null;
  };
  const validationMessage = getValidationMessage();

  return (
    <div className={className}>
      {label && <Label className="mb-2 block">{label}</Label>}
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Scale className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="number"
            value={displayValue}
            onChange={(e) => handleValueChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={getPlaceholder()}
            className="pl-10"
            step={currentUnit === 'kg' ? '0.1' : '0.2'}
            min="0"
          />
        </div>
        
        {allowUnitChange && (
          <Select value={currentUnit} onValueChange={handleUnitChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="kg">kg</SelectItem>
              <SelectItem value="lbs">lbs</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {!allowUnitChange && (
          <div className="flex items-center justify-center w-12 text-sm text-muted-foreground">
            {currentUnit}
          </div>
        )}
      </div>

      {validationMessage && (
        <p className="mt-1 text-sm text-yellow-600">{validationMessage}</p>
      )}

      {value > 0 && currentUnit !== 'kg' && (
        <p className="mt-1 text-xs text-muted-foreground">
          {value.toFixed(1)} kg
        </p>
      )}
    </div>
  );
};
